<template>
    <div class="admin-upload-image" :class="rootClass">
        <div
            class="admin-upload-image__drop"
            :class="{
                'admin-upload-image__drop--drag': dragOver,
                'admin-upload-image__drop--filled': !!displaySrc,
                'admin-upload-image__drop--pending': !!source
            }"
            role="button"
            tabindex="0"
            @click="openFileDialog"
            @keydown.enter.prevent="openFileDialog"
            @keydown.space.prevent="openFileDialog"
            @dragover.prevent="dragOver = true"
            @dragleave.prevent="onDragLeave"
            @drop.prevent="onFileDrop"
        >
            <input
                :id="id"
                ref="fileInput"
                type="file"
                class="admin-upload-image__native-input"
                accept=".jpg,.jpeg,.png,.gif,image/jpeg,image/png,image/gif"
                @change="onFileChange"
            />

            <div class="admin-upload-image__preview">
                <img
                    v-if="displaySrc && !previewError"
                    :src="displaySrc"
                    alt=""
                    class="admin-upload-image__img"
                    @error="previewError = true"
                />
                <div v-else-if="displaySrc && previewError" class="admin-upload-image__empty admin-upload-image__empty--error">
                    <span class="admin-upload-image__empty-ring">
                        <admin-icon name="image" aria-hidden="true"></admin-icon>
                    </span>
                    <span class="admin-upload-image__empty-title">{{ trans('general.image_load_failed') }}</span>
                    <span class="admin-upload-image__empty-hint">{{ trans('general.choose_image') }}</span>
                </div>
                <div v-else class="admin-upload-image__empty">
                    <span class="admin-upload-image__empty-ring">
                        <admin-icon name="upload" aria-hidden="true"></admin-icon>
                    </span>
                    <span class="admin-upload-image__empty-title">{{ trans('general.choose_image') }}</span>
                    <span class="admin-upload-image__empty-hint admin-upload-image__empty-hint--muted">{{ trans('general.drop_or_click') }}</span>
                </div>
            </div>

            <div v-if="displaySrc && !source && !previewError" class="admin-upload-image__overlay" aria-hidden="true">
                <span class="admin-upload-image__overlay-inner">
                    <admin-icon name="camera" aria-hidden="true"></admin-icon>
                    <span>{{ trans('general.update') }}</span>
                </span>
            </div>
        </div>

        <div v-if="defaultImage && !source" class="admin-upload-image__actions">
            <button
                type="button"
                class="btn btn-sm admin-upload-image__btn-remove"
                v-confirm="{ ok: confirmRemove() }"
            >
                <admin-icon name="trash" aria-hidden="true"></admin-icon>
                {{ trans('general.remove') }}
            </button>
        </div>

        <div v-if="source" class="admin-upload-image__pending">
            <div class="admin-upload-image__pending-actions">
                <button type="button" class="btn btn-sm admin-toolbar-btn admin-toolbar-btn--solid" @click.stop="uploadImage">
                    {{ trans('general.upload') }}
                </button>
                <button type="button" class="btn btn-sm admin-upload-image__btn-ghost" @click.stop="cancelUploadImage">
                    {{ trans('general.cancel_upload') }}
                </button>
            </div>
        </div>
    </div>
</template>

<script>
    export default {
        props: {
            id: {
                default: ''
            },
            uploadPath: {
                required: true
            },
            removePath: {
                required: true
            },
            imageSource: {
                default: ''
            },
            /** 'avatar' = circular hero; 'media' = rounded rectangle (logos, etc.) */
            presentation: {
                type: String,
                default: 'media'
            }
        },
        data() {
            return {
                source: '',
                uploaded: '',
                isRemoved: false,
                pendingFile: null,
                dragOver: false,
                previewError: false,
            };
        },
        watch: {
            imageSource() {
                this.previewError = false;
            },
            uploaded() {
                this.previewError = false;
            },
            source() {
                this.previewError = false;
            },
        },
        computed: {
            rootClass() {
                return this.presentation === 'avatar' ? 'admin-upload-image--avatar' : 'admin-upload-image--media';
            },
            defaultImage() {
                return ((this.imageSource && !this.isRemoved) || this.uploaded) ? true : false;
            },
            resolvedPath() {
                const path = this.uploaded || this.imageSource;
                if (!path) {
                    return '';
                }
                const s = String(path);
                if (/^https?:\/\//i.test(s)) {
                    return s;
                }
                return '/' + s.replace(/^\//, '');
            },
            displaySrc() {
                if (this.source) {
                    return this.source;
                }
                if (this.defaultImage && this.resolvedPath) {
                    return this.resolvedPath;
                }
                return '';
            }
        },
        methods: {
            openFileDialog() {
                if (this.$refs.fileInput) {
                    this.$refs.fileInput.click();
                }
            },
            onDragLeave(e) {
                if (!e.currentTarget.contains(e.relatedTarget)) {
                    this.dragOver = false;
                }
            },
            onFileDrop(e) {
                this.dragOver = false;
                const files = e.dataTransfer && e.dataTransfer.files;
                if (files && files[0]) {
                    this.validateAndLoad(files[0]);
                }
            },
            onFileChange(e) {
                const files = e.target.files;
                if (files && files[0]) {
                    this.validateAndLoad(files[0]);
                }
            },
            validateAndLoad(file) {
                const allowedExtensions = /(\.jpg|\.jpeg|\.png|\.gif)$/i;
                if (!allowedExtensions.exec(file.name)) {
                    toastr.error(i18n.general.file_not_supported);
                    return;
                }
                this.pendingFile = file;
                this.createImage(file);
            },
            createImage(file) {
                const reader = new FileReader();
                reader.onload = (ev) => {
                    this.source = ev.target.result;
                };
                reader.readAsDataURL(file);
            },
            cancelUploadImage() {
                this.source = '';
                this.pendingFile = null;
                if (this.$refs.fileInput) {
                    this.$refs.fileInput.value = '';
                }
            },
            getFileForUpload() {
                if (this.pendingFile) {
                    return this.pendingFile;
                }
                const input = this.$refs.fileInput;
                if (input && input.files && input.files[0]) {
                    return input.files[0];
                }
                return null;
            },
            uploadImage() {
                const file = this.getFileForUpload();
                if (!file) {
                    return;
                }
                const data = new FormData();
                data.append('image', file);
                axios.post('/api' + this.uploadPath, data)
                    .then(response => response.data)
                    .then(response => {
                        toastr.success(response.message);
                        this.source = '';
                        this.pendingFile = null;
                        if (this.$refs.fileInput) {
                            this.$refs.fileInput.value = '';
                        }
                        this.uploaded = response.image;
                        this.$emit('uploaded', response.image);
                    }).catch(error => {
                        if (error.response && error.response.status === 422) {
                            toastr.error(error.response.data.error);
                            this.cancelUploadImage();
                        } else if (error.response && (error.response.status === 413 || error.response.status === 500)) {
                            toastr.error(i18n.general.file_too_large);
                            this.cancelUploadImage();
                        } else {
                            helper.showDataErrorMsg(error);
                        }
                    });
            },
            confirmRemove() {
                return dialog => this.removeImage();
            },
            removeImage() {
                axios.delete('/api' + this.removePath)
                    .then(response => response.data)
                    .then(response => {
                        toastr.success(response.message);
                        this.isRemoved = true;
                        this.uploaded = '';
                        this.$emit('removed', '');
                    }).catch(error => {
                        if (error.response && error.response.status === 422) {
                            toastr.error(error.response.data.error);
                            this.cancelUploadImage();
                        } else {
                            helper.showDataErrorMsg(error);
                        }
                    });
            }
        }
    };
</script>
